import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  ContentSectionHeader,
  Hyperlink,
  InformationSnippetGroup,
  InlineAlert,
  PageHeader,
  SpinningLoader,
  Toast,
  VerticalTabs,
} from 'br2s-ui-componentlibrary_r2_02';
import { SystemIcon } from 'br2s-ui-componentlibrary_r2_02/dist/foundation/assets/system-icon';
import { LocalStorageService, useHTTPError } from 'br2s-ui-common-utils-02';
import { AccountAPI } from '@/services/api/accounts/accounts';
import { ACCOUNT_SETTINGS } from '@/utilities/constants';
import {
  formatAddress,
  formatDate,
  formatMobileNumber,
} from '@/utilities/utilities';
import Container from '@/components/Container/Container';

import './AccountSettings.scss';
import {
  extractCodeValue,
  getContactApiType,
  getUpdateDetailsLink,
  retrieveContactInfoFromResponse,
} from './accountSettingsUtils';
import { CodesAPI } from '@/services/api/codes/codes';
import { useStore } from '@/redux/store';
import { isAxiosError } from 'axios';
import { ToastMessage } from '../../../types/index';
export type Code = {
  value: string;
  description: string;
};

interface UserPayload {
  peopleProfileId: string;
  uin: string;
  idType: string;
  nationality: string;
  name: string;
  birthDate: string;
  blockNo: string;
  levelNo: string;
  unitNo: string;
  streetName: string;
  buildingName: string;
  postalCode: string;
  foreignAddr1: string;
  foreignAddr2: string;
}

const defaultUserPayload = {
  peopleProfileId: '',
  uin: '',
  idType: '',
  nationality: '',
  name: '',
  birthDate: '',
  blockNo: '',
  levelNo: '',
  unitNo: '',
  streetName: '',
  buildingName: '',
  postalCode: '',
  foreignAddr1: '',
  foreignAddr2: '',
};

interface UserProfileProps {
  name: string;
  idNumber: string;
  idType: string;
  nationality: string;
  birthDate: string;
  postalCode: string;
  blockNo: string;
  levelNo: string;
  unitNo: string;
  streetName: string;
  buildingName: string;
  contactPostalCode: string;
  contactBlockNo: string;
  contactLevelNo: string;
  contactUnitNo: string;
  contactStreetName: string;
  contactBuildingName: string;
  contactForeignAddr1?: string;
  contactForeignAddr2?: string;
}

export interface ResultItem {
  idNumber: string;
  email: string;
  emailVerifiedFlag: boolean;
  mobileNo: string;
  mobileNoCountryCode?: string;
  profileId?: string;
  officerCategory?: string;
}

const defaultUserProfile = {
  name: '',
  idNumber: '',
  idType: '',
  nationality: '',
  birthDate: '',
  postalCode: '',
  blockNo: '',
  levelNo: '',
  unitNo: '',
  streetName: '',
  buildingName: '',
  contactPostalCode: '',
  contactBlockNo: '',
  contactLevelNo: '',
  contactUnitNo: '',
  contactStreetName: '',
  contactBuildingName: '',
};

const defaultContactInfo = {
  idNumber: '',
  email: '',
  emailVerifiedFlag: false,
  mobileNo: '',
};

const contactApi = {
  POSITION_HOLDER: AccountAPI.getPHContactInfo,
  RQI: AccountAPI.getRQIContactInfo,
  GC: AccountAPI.getGCContactInfo,
};

const initialLoadingState = {
  particulars: true,
  addresses: true,
  contactInfo: true,
};

const AccountSettings = () => {
  const navigate = useNavigate();
  const { handleHTTPError } = useHTTPError();
  const { auth } = useStore();
  const { origin } = LocalStorageService.getAuthInfo('origin');
  const [activeIndex, setActiveIndex] = useState(0);
  const [userPayload, setUserPayload] =
    useState<UserPayload>(defaultUserPayload);
  const [userProfile, setuserProfile] =
    useState<UserProfileProps>(defaultUserProfile);
  const [contactInfo, setContactInfo] = useState<ResultItem | undefined>(
    defaultContactInfo,
  );
  const [loading, setLoading] = useState(initialLoadingState);
  const [toastMessage, setToastMessage] = useState<ToastMessage | null>(null);
  const closeToast = () => setToastMessage(null);

  // istanbul ignore next
  useEffect(() => {
    if (auth.authenticated && auth.hasProfileAccess) {
      getUserPayload();
      getProfile();
      getContactInfo();
    } else if (auth.hasProfileAccess === false) navigate('/unauthorized');
  }, [auth.authenticated, auth.hasProfileAccess]);

  if (!LocalStorageService.hasAuth())
    return <Navigate to="/unauthorized" replace />;

  // istanbul ignore next
  const getIASCodes = async (userResult: UserPayload) => {
    try {
      const response = await Promise.all([
        CodesAPI.getCodes('nationality'),
        CodesAPI.getCodes('id-type'),
      ]);

      if (response.every((res) => res.status === 200)) {
        const [nationalityRes, idTypeRes] = response;
        const nationality = extractCodeValue(
          nationalityRes.data.result?.codeList,
          userResult.nationality,
        );
        const idType = extractCodeValue(
          idTypeRes.data.result?.codeList,
          userResult.idType,
        );

        setUserPayload((userPayload) => ({
          ...userPayload,
          ...userResult,
          nationality,
          idType,
        }));
      }
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 400) {
        setToastMessage({
          type: 'error',
          description: error?.response?.data?.error.errorDescription,
        });
      }
      handleHTTPError(error, { errorType: 'none' });
      setUserPayload((userPayload) => ({ ...userPayload, ...userResult }));
    } finally {
      setLoading((loading) => ({ ...loading, particulars: false }));
    }
  };

  // istanbul ignore next
  const getUserPayload = async () => {
    try {
      const response = await AccountAPI.getUserPayload();
      if (response?.status === 200) {
        getIASCodes(response.data.result);
      }
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 400) {
        setToastMessage({
          type: 'error',
          description: error?.response?.data?.error.errorDescription,
        });
      }
      handleHTTPError(error);
      setLoading((loading) => ({ ...loading, particulars: false }));
    }
  };

  // istanbul ignore next
  const getProfile = async () => {
    try {
      const response = await AccountAPI.getUserProfile();
      if (response?.status === 200) {
        setuserProfile((userProfile) => ({
          ...userProfile,
          ...response.data?.result?.peopleProfiles?.[0],
        }));
      }
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 400) {
        setToastMessage({
          type: 'error',
          description: error?.response?.data?.error.errorDescription,
        });
      }
      handleHTTPError(error);
    } finally {
      setLoading((loading) => ({ ...loading, addresses: false }));
    }
  };

  // istanbul ignore next
  const getContactInfo = async () => {
    if (origin === 'SP') return;

    try {
      const apiType = getContactApiType();
      const response = await contactApi[apiType]();
      if (response?.status === 200) {
        const responseData = retrieveContactInfoFromResponse(response, apiType);
        setContactInfo((contactInfo) => ({ ...contactInfo, ...responseData }));
      }
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 400) {
        setToastMessage({
          type: 'error',
          description: error?.response?.data?.error.errorDescription,
        });
      }
      handleHTTPError(error, {
        errorType: 'inline',
      });
    } finally {
      setLoading((loading) => ({ ...loading, contactInfo: false }));
    }
  };

  const { link: updateDetailsLink, label: updateDetailsLabel } =
    getUpdateDetailsLink();

  return (
    <div className="my-profile-container">
      <PageHeader
        id="page-header"
        title="My profile"
        breadcrumbData={[
          {
            label: 'Home',
            onClick: () => navigate('/'),
          },
          {
            label: 'My profile',
          },
        ]}
      />
      <Container>
        <div className="content-container container">
          <div className="myprofile-page-content">
            <div className="content-header-title">
              <ContentSectionHeader
                id="header-title"
                headerLabel={userPayload?.name?.toUpperCase()}
                level={1}
                secondaryDescription={userPayload?.uin}
              />
            </div>
            <VerticalTabs
              id="account-settings-vertical-tabs"
              isHaveBorder={true}
              tabs={[
                {
                  id: 0,
                  label: ACCOUNT_SETTINGS.TITLE.label,
                  iconClassName: 'icon-sys icon-sys-document-profile_tag',
                  onClick: () => setActiveIndex(0),
                },
              ]}
              activeIndex={activeIndex}
              children={
                <div className="account-map-tabs-content">
                  <ContentSectionHeader
                    id="account-information"
                    headerLabel={ACCOUNT_SETTINGS.TITLE.label}
                    level={2}
                    secondaryDescription={ACCOUNT_SETTINGS.TITLE.description}
                    titleIcon={SystemIcon.DocumentProfileTag}
                  />
                  <div className="information-container">
                    <div className="area-container">
                      <ContentSectionHeader
                        id="user-particulars"
                        headerLabel={ACCOUNT_SETTINGS.PARTICULARS.label}
                        level={3}
                      />
                      {loading.particulars ? (
                        <SpinningLoader />
                      ) : (
                        <InformationSnippetGroup
                          layout="horizontal"
                          data={[
                            {
                              label: 'Name',
                              children: userPayload?.name,
                            },
                            {
                              label: 'Identification number',
                              children: userPayload?.uin,
                            },
                            {
                              label: 'Identification type',
                              children: userPayload?.idType,
                            },
                            {
                              label: 'Nationality',
                              children: userPayload?.nationality,
                            },
                            {
                              label: 'Date of birth',
                              children: formatDate(userPayload?.birthDate),
                            },
                          ]}
                        />
                      )}
                    </div>
                    <div className="area-container">
                      <ContentSectionHeader
                        id="user-addresses"
                        headerLabel={ACCOUNT_SETTINGS.ADDRESSES.label}
                        level={3}
                      />
                      {loading.addresses || loading.particulars ? (
                        <SpinningLoader />
                      ) : (
                        <InformationSnippetGroup
                          layout="horizontal"
                          data={[
                            {
                              label: 'Residential address',
                              children: formatAddress(userPayload),
                            },
                            {
                              label: 'Contact address',
                              children: formatAddress({
                                blockNo: userProfile?.contactBlockNo,
                                levelNo: userProfile?.contactLevelNo,
                                unitNo: userProfile?.contactUnitNo,
                                streetName: userProfile?.contactStreetName,
                                buildingName: userProfile?.contactBuildingName,
                                postalCode: userProfile?.contactPostalCode,
                                foreignAddr1: userProfile?.contactForeignAddr1,
                                foreignAddr2: userProfile?.contactForeignAddr2,
                              }),
                            },
                          ]}
                        />
                      )}
                    </div>

                    {origin === 'CP' && (
                      <div className="area-container">
                        <ContentSectionHeader
                          id="user-addresses"
                          headerLabel={ACCOUNT_SETTINGS.CONTACT.label}
                          level={3}
                        />
                        {loading.contactInfo ? (
                          <SpinningLoader />
                        ) : (
                          <InformationSnippetGroup
                            layout="horizontal"
                            data={[
                              {
                                label: 'Mobile number',
                                children: formatMobileNumber(
                                  contactInfo?.mobileNo,
                                  contactInfo?.mobileNoCountryCode,
                                ),
                              },
                              {
                                label: 'Email',
                                children: contactInfo?.email,
                              },
                            ]}
                          />
                        )}
                      </div>
                    )}
                    {origin !== 'SP' && (
                      <InlineAlert
                        id="account-alert"
                        type="informative"
                        headline="Edit information"
                        children={
                          <div>
                            {ACCOUNT_SETTINGS.ALERT.description}
                            <Hyperlink
                              id="link-inline"
                              href={updateDetailsLink}
                              label={updateDetailsLabel}
                            />
                            {ACCOUNT_SETTINGS.ALERT.addtionalDesc}
                          </div>
                        }
                      />
                    )}
                  </div>
                </div>
              }
            />
          </div>
        </div>

        <Toast
          type={toastMessage?.type}
          isDisplayed={!!toastMessage}
          headline=""
          description={toastMessage?.description}
          onDismissClick={closeToast}
          onDismiss={closeToast}
        />
      </Container>
    </div>
  );
};

export default AccountSettings;
